import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Modal, Button, Upload, message, Space, Typography, Image, Switch, Radio } from 'antd';
import {
  UploadOutlined,
  ZoomInOutlined,
  ScissorOutlined,
  PlusOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { uploadImage } from '@/api/common';
import { uploadResourcePositionImage } from '@/api/upload';
import styles from './ImageCropper.module.less';

const { Text } = Typography;

interface ImageCropperProps {
  value?: string; // 回显的图片 URL
  onChange?: (url: string) => void; // 图片改变时的回调
  onCropComplete?: (
    croppedImageBlob: Blob | HTMLCanvasElement,
    croppedImageUrl: string
  ) => void;
  onCropCancel?: () => void;
  aspectRatio?: number;
  width?: number;
  height?: number;
  quality?: number;
  outputType?: 'blob' | 'dataURL';
  fixedCropBox?: boolean; // 是否固定裁剪框大小
  cropBoxWidth?: number; // 固定裁剪框宽度
  cropBoxHeight?: number; // 固定裁剪框高度
  // 上传和回显相关 props
  autoUpload?: boolean; // 是否自动上传裁剪后的图片
  uploadApi?: (file: File) => Promise<any>; // 自定义上传接口
  useActivityUpload?: boolean; // 是否使用活动通知的上传接口（uploadImage），默认 false 使用 uploadResourcePositionImage
  onUploadSuccess?: (url: string, ratio?: number) => void; // 上传成功回调
  onUploadError?: (error: any) => void; // 上传失败回调
  showCroppedPreview?: boolean; // 是否显示裁剪后的预览
  picDesc?: string; // 点击上传之后的提示文本
  enablePreview?: boolean; // 是否启用查看大图功能
  // 文件配置
  fileTypes?: string[]; // 支持的文件类型数组
  maxFileSizeMB?: number; // 最大文件大小限制（MB）
  enableDragUpload?: boolean; // 是否启用拖拽上传
  noCrop?: boolean; // 是否不裁剪，直接上传原图（已废弃，使用 enableModeSwitch 替代）
  disabled?: boolean; // 是否禁用（查看模式）
  enableModeSwitch?: boolean; // 是否启用模式切换（直接上传/裁剪上传）
  defaultUploadMode?: 'direct' | 'crop'; // 默认上传模式：'direct' 直接上传，'crop' 裁剪上传
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  value,
  onChange,
  onCropComplete,
  onCropCancel,
  aspectRatio = 1,
  width = 200,
  height = 200,
  quality = 1.0, // 最高质量，无压缩损失
  outputType = 'blob',
  fixedCropBox = false,
  cropBoxWidth = 200,
  cropBoxHeight = 200,
  // 上传和回显相关参数
  autoUpload = false,
  uploadApi,
  useActivityUpload = false, // 默认不使用活动通知的上传接口
  onUploadSuccess,
  onUploadError,
  showCroppedPreview = false,
  picDesc,
  enablePreview = false, // 默认不启用查看大图
  // 文件配置
  fileTypes = ['image/webp'],
  maxFileSizeMB = 1,
  enableDragUpload = true, // 默认启用拖拽上传
  noCrop = false, // 默认需要裁剪（已废弃，使用 enableModeSwitch 替代）
  disabled = false, // 默认不禁用
  enableModeSwitch = false, // 默认不启用模式切换
  defaultUploadMode = 'crop', // 默认裁剪上传
}) => {
  const [visible, setVisible] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string>('');
  const [imageResolution, setImageResolution] = useState<{ width: number; height: number } | null>(null);
  // 上传模式：'direct' 直接上传，'crop' 裁剪上传
  const [uploadMode, setUploadMode] = useState<'direct' | 'crop'>(
    enableModeSwitch ? defaultUploadMode : (noCrop ? 'direct' : 'crop')
  );
  // const [dragMode, setDragMode] = useState<'move' | 'crop'>('move'); // 拖拽模式：move=移动图片，crop=创建裁剪区域
  const cropperRef = useRef<any>(null);

  // 根据 useActivityUpload 参数决定使用哪个默认上传接口
  const defaultUploadApi = useCallback(async (file: File) => {
    if (useActivityUpload) {
      // 活动通知使用 uploadImage 接口
      const resp = await uploadImage(file);
      if (resp.status !== 200) {
        throw new Error(resp.message || '上传失败');
      }
      // 返回 ImageCropper 期望的格式，同时携带 ratio
      return {
        status: 200,
        data: resp.data.imageUrl, // ImageCropper 期望 data 是字符串 URL
        ratio: resp.data.ratio, // 携带 ratio 供父组件使用
      };
    } else {
      // 其他情况使用 uploadResourcePositionImage 接口
      return await uploadResourcePositionImage(file);
    }
  }, [useActivityUpload]);

  // 使用传入的 uploadApi 或默认的上传接口
  const finalUploadApi = uploadApi || defaultUploadApi;

  // 当 value 变化时，同步到 croppedPreviewUrl（用于回显）
  useEffect(() => {
    if (value) {
      setCroppedPreviewUrl(value);
    }
  }, [value]);

  // 获取图片分辨率的函数
  const getImageResolution = useCallback((imageUrl: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error('无法加载图片'));
      };
      img.src = imageUrl;
    });
  }, []);

  // 当 imageSrc 变化时，获取图片分辨率
  useEffect(() => {
    if (imageSrc) {
      getImageResolution(imageSrc)
        .then((resolution) => {
          setImageResolution(resolution);
        })
        .catch((error) => {
          console.warn('获取图片分辨率失败:', error);
          setImageResolution(null);
        });
    } else {
      setImageResolution(null);
    }
  }, [imageSrc, getImageResolution]);

  // 常量配置 - 移除 maxWidth 和 maxHeight 限制，保持原始裁剪区域的分辨率以获得更清晰的图片
  // 启用高质量图像平滑以兼容大部分图像类型，获得更好的渲染质量
  const CROPPER_CONFIG = {
    imageSmoothingEnabled: true, // 启用平滑以获得更好的图像质量，兼容大部分图像
    imageSmoothingQuality: 'high' as const, // 使用最高质量的平滑算法
    // 可选：如果需要限制最大尺寸，可以取消下面的注释
    // maxWidth: width,
    // maxHeight: height,
  };

  // 始终使用 WebP 格式输出（质量更好，文件更小）
  const getOutputFormat = useCallback(() => {
    // WebP 格式支持质量参数，范围 0-1
    // 为了完全保持原画质，始终使用最高质量 1.0
    // 这样可以确保没有任何压缩损失
    return { mimeType: 'image/webp', quality: 1.0 };
  }, []);

  const FILE_CONFIG = {
    name: 'cropped-image.jpg',
    type: 'image/jpeg' as const,
  };

  // 重置组件状态
  const resetComponentState = useCallback(() => {
    setVisible(false);
    setImageSrc('');
    setOriginalFile(null);
  }, []);

  // 通用的上传处理函数
  const handleUpload = useCallback(
    async (blob: Blob) => {
      try {
        setUploading(true);
        
        // 验证 blob 是否有内容
        if (blob.size === 0) {
          throw new Error('裁剪后的图片为空');
        }

        // 使用 blob 的实际类型，而不是 getOutputFormat() 返回的类型
        // 这样可以确保使用最新的裁剪后的文件类型
        const blobType = blob.type || 'image/webp'; // 如果 blob 没有 type，使用默认值
        const fileExtension = blobType === 'image/webp' ? 'webp' : 
                             blobType === 'image/png' ? 'png' : 
                             blobType === 'image/jpeg' ? 'jpg' : 'jpg';
        
        // 使用 blob 的实际类型创建 File 对象，确保是最新的裁剪后文件
        const file = new File([blob], `cropped-image-${Date.now()}.${fileExtension}`, {
          type: blobType,
          lastModified: Date.now(), // 确保使用当前时间戳
        });

        // 使用 finalUploadApi 而不是 uploadApi，确保使用正确的上传接口
        const result = await finalUploadApi(file);

        if (result.status === 200 && result.data) {
          message.success('裁剪并上传成功！');
          const imageUrl = result.data;
          onUploadSuccess?.(imageUrl, result.ratio);
          setCroppedPreviewUrl(imageUrl);
          // 如果有 onChange，调用它更新 value
          onChange?.(imageUrl);
          // 上传成功后关闭弹窗
          resetComponentState();
          return true;
        } else {
          message.error(result.message || '上传失败');
          onUploadError?.(result);
          return false;
        }
      } catch (error) {
        console.error('上传失败:', error);
        message.error('上传失败');
        onUploadError?.(error);
        return false;
      } finally {
        setUploading(false);
      }
    },
    [finalUploadApi, onUploadSuccess, onUploadError, onChange, getOutputFormat]
  );

  // 通用的裁剪处理函数
  const processCroppedImage = useCallback(
    async (croppedCanvas: HTMLCanvasElement) => {
      const { mimeType, quality: outputQuality } = getOutputFormat();
      const croppedImageUrl = croppedCanvas.toDataURL(mimeType, outputQuality);

      // 显示裁剪后的预览
      if (showCroppedPreview) {
        setCroppedPreviewUrl(croppedImageUrl);
      }

      // 如果启用自动上传
      if (autoUpload) {
        const success = await new Promise<boolean>((resolve) => {
          croppedCanvas.toBlob(
            async (blob: Blob | null) => {
              if (blob) {
                const uploadSuccess = await handleUpload(blob);
                resolve(uploadSuccess);
              } else {
                resolve(false);
              }
            },
            mimeType,
            outputQuality
          );
        });

        if (!success) {
          return;
        }
      }

      // 调用完成回调
      onCropComplete?.(croppedCanvas, croppedImageUrl);

      // 如果不是自动上传，关闭弹窗
      if (!autoUpload) {
        resetComponentState();
      }
    },
    [quality, showCroppedPreview, autoUpload, handleUpload, onCropComplete, getOutputFormat]
  );

  // 处理文件上传
  const handleFileUpload = useCallback(
    async (file: File) => {
      console.log('开始处理文件上传:', file);

      // 检查文件大小
      if (maxFileSizeMB && file.size / 1024 / 1024 > maxFileSizeMB) {
        message.error(`文件大小不能超过 ${maxFileSizeMB}MB`);
        return false;
      }

      // 检查文件类型
      if (!fileTypes.includes(file.type)) {
        const allowedTypes = fileTypes
          .map((type) => type.split('/')[1].toUpperCase())
          .join('、');
        message.error(`只能上传 ${allowedTypes} 格式的图片文件`);
        return false;
      }

      // 如果选择直接上传模式，直接上传原文件
      const shouldDirectUpload = enableModeSwitch ? uploadMode === 'direct' : noCrop;
      if (shouldDirectUpload) {
        try {
          // 检查图片分辨率
          const fileUrl = URL.createObjectURL(file);
          const resolution = await getImageResolution(fileUrl);
          URL.revokeObjectURL(fileUrl); // 释放临时 URL
          
          // 检查分辨率是否匹配
          if (resolution.width !== width || resolution.height !== height) {
            // 使用 Promise 等待用户确认
            const shouldContinue = await new Promise<boolean>((resolve) => {
              Modal.confirm({
                title: '分辨率不匹配',
                content: (
                  <div>
                    <p>当前图片分辨率：<strong>{resolution.width} × {resolution.height}px</strong></p>
                    <p>期望分辨率：<strong>{width} × {height}px</strong></p>
                    <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                      分辨率不匹配，建议使用"裁剪"模式调整分辨率后再上传。
                    </p>
                    <p style={{ marginTop: 8 }}>是否继续上传？</p>
                  </div>
                ),
                okText: '继续上传',
                cancelText: '取消',
                onOk: () => {
                  resolve(true);
                },
                onCancel: () => {
                  resolve(false);
                },
              });
            });
            
            if (!shouldContinue) {
              return false;
            }
            
            // 如果用户选择继续，给出警告提示
            message.warning({
              content: `图片分辨率不匹配（当前：${resolution.width} × ${resolution.height}px，期望：${width} × ${height}px），建议使用裁剪模式调整分辨率`,
              duration: 5,
            });
          }
          
          setUploading(true);
          const result = await finalUploadApi(file);
          
          if (result.status === 200 && result.data) {
            message.success('上传成功！');
            const imageUrl = result.data;
            onUploadSuccess?.(imageUrl, result.ratio);
            setCroppedPreviewUrl(imageUrl);
            onChange?.(imageUrl);
            return false;
          } else {
            message.error(result.message || '上传失败');
            onUploadError?.(result);
            return false;
          }
        } catch (error) {
          console.error('上传失败:', error);
          message.error('上传失败');
          onUploadError?.(error);
          return false;
        } finally {
          setUploading(false);
        }
      }

      // 需要裁剪，打开裁剪弹窗
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        console.log('文件读取完成，设置图片源:', result ? '成功' : '失败');
        setImageSrc(result);
        setOriginalFile(file);
        setVisible(true);
      };
      reader.onerror = () => {
        console.error('文件读取失败');
        message.error('文件读取失败');
      };
      reader.readAsDataURL(file);

      return false; // 阻止默认上传
    },
    [maxFileSizeMB, fileTypes, noCrop, enableModeSwitch, uploadMode, width, height, getImageResolution, finalUploadApi, onUploadSuccess, onUploadError, onChange]
  );

  // 处理裁剪完成
  const handleCropComplete = useCallback(async () => {
    if (!cropperRef.current) return;

    const cropper = cropperRef.current.cropper;
    
    // 1. 获取原始图像的真实尺寸
    const imageData = cropper.getImageData();
    const originalWidth = imageData.naturalWidth;
    const originalHeight = imageData.naturalHeight;  

    // 如果原始图像尺寸过小，直接使用原始尺寸裁剪
    if (originalWidth < width || originalHeight < height) {
        message.warning({ 
          content: '原始图像尺寸小于目标尺寸，可能会影响清晰度。', 
          duration: 3
        });
    }  

    // 2. 使用期望的目标尺寸作为输出尺寸
    // 直接用 cropBoxWidth/cropBoxHeight 作为输出，让 cropper.js 负责缩放
    // 即使原图小于目标尺寸，也输出目标尺寸，避免输出分辨率不足导致模糊
    const finalOutputWidth = fixedCropBox ? cropBoxWidth : width;
    const finalOutputHeight = fixedCropBox ? cropBoxHeight : height;

    // 3. 获取高质量的裁剪 Canvas
    const croppedCanvas = cropper.getCroppedCanvas({
      width: finalOutputWidth,
      height: finalOutputHeight,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });  

    if (!croppedCanvas) {
      message.error('裁剪失败，无法获取 Canvas 对象。');
      return;
    }  

    const { mimeType, quality: outputQuality } = getOutputFormat();
    
    if (outputType === 'dataURL') {
      await processCroppedImage(croppedCanvas);
    } else {
      // 使用 Promise 封装 toBlob，使其可以被 await
      const blob = await new Promise<Blob | null>((resolve) => {
        croppedCanvas.toBlob(
          (blob) => resolve(blob),
          mimeType,
          outputQuality
        );
      });  

      if (blob) {
        const croppedImageUrl = URL.createObjectURL(blob);  

        // 显示裁剪后的预览
        if (showCroppedPreview) {
          setCroppedPreviewUrl(croppedImageUrl);
        }  

        // 如果启用自动上传
        if (autoUpload) {
          const success = await handleUpload(blob);
          if (!success) return;
        }  

        // 调用完成回调
        onCropComplete?.(blob, croppedImageUrl);  

        // 如果不是自动上传，关闭弹窗
        if (!autoUpload) {
          resetComponentState();
        }
      } else {
        message.error('裁剪失败，无法生成 Blob 对象。');
      }


    }
  }, [
    outputType,
    quality,
    showCroppedPreview,
    autoUpload,
    handleUpload,
    onCropComplete,
    processCroppedImage,
    getOutputFormat,
    width,
    height,
  ]);

  // 处理取消裁剪
  const handleCropCancel = useCallback(() => {
    resetComponentState();
    onCropCancel?.();
  }, [onCropCancel, resetComponentState]);

  // 重置裁剪区域
  const handleReset = useCallback(() => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      cropper.reset();
      
      // 重置时重新校准裁剪框位置
      if (imageResolution) {
        // 当图片分辨率与期望分辨率一致时，强制裁剪框匹配图片尺寸
        if (imageResolution.width === width && imageResolution.height === height) {
          const containerData = cropper.getContainerData();
          const imageData = cropper.getImageData();
          
          // 计算居中的裁剪框位置
          const cropBoxData = {
            left: (containerData.width - imageData.width) / 2,
            top: (containerData.height - imageData.height) / 2,
            width: imageData.width,
            height: imageData.height,
          };
          cropper.setCropBoxData(cropBoxData);
          // 确保图片不缩放
          cropper.zoomTo(0);
        }
      }
    }
  }, [imageResolution, width, height]);

  // 确定显示哪个图片：优先显示 value（回显），其次显示裁剪后的预览
  const displayImageUrl = value || (showCroppedPreview && croppedPreviewUrl ? croppedPreviewUrl : '');

  // 处理点击图片重新上传/裁剪
  const handleImageClick = useCallback(() => {
    // 如果选择直接上传模式，直接触发文件选择
    const shouldDirectUpload = enableModeSwitch ? uploadMode === 'direct' : noCrop;
    if (shouldDirectUpload) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = fileTypes.join(',');
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          handleFileUpload(file);
        }
      };
      input.click();
      return;
    }
    
    // 如果需要裁剪，且有原始文件，直接打开裁剪弹窗
    if (originalFile) {
      setVisible(true);
    } else {
      // 触发文件选择
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = fileTypes.join(',');
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          handleFileUpload(file);
        }
      };
      input.click();
    }
  }, [originalFile, fileTypes, handleFileUpload, noCrop, enableModeSwitch, uploadMode]);

  // 处理拖拽上传
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragUpload || visible) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  }, [enableDragUpload, visible]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragUpload || visible) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  }, [enableDragUpload, visible]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!enableDragUpload || visible) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // 检查文件类型
      if (fileTypes.includes(file.type)) {
        handleFileUpload(file);
      } else {
        message.error(`只能上传 ${fileTypes.map((type) => type.split('/')[1].toUpperCase()).join('、')} 格式的图片文件`);
      }
    }
  }, [enableDragUpload, visible, fileTypes, handleFileUpload]);

  // 初始化裁剪框位置和大小
  const initCropper = useCallback(() => {
    if (!cropperRef.current || !imageResolution) return;
    
    const cropper = cropperRef.current.cropper;
    if (!cropper) return;

    // 获取容器和图片数据
    const containerData = cropper.getContainerData();
    const imageData = cropper.getImageData();
    
    // 关键修复：当图片分辨率与期望分辨率一致时
    if (imageResolution.width === width && imageResolution.height === height) {
      // 1. 重置缩放为0（不缩放）
      cropper.zoomTo(0);
      
      // 2. 计算裁剪框位置（居中）
      const cropBoxLeft = (containerData.width - imageData.width) / 2;
      const cropBoxTop = (containerData.height - imageData.height) / 2;
      
      // 3. 设置裁剪框完全匹配图片尺寸和位置
      const cropBoxData = {
        left: cropBoxLeft,
        top: cropBoxTop,
        width: imageData.width,
        height: imageData.height,
      };
      
      cropper.setCropBoxData(cropBoxData);
      
      // 4. 确保图片位置居中
      cropper.moveTo(
        (containerData.width - imageData.width) / 2,
        (containerData.height - imageData.height) / 2
      );
    } else if (fixedCropBox) {
      // 固定裁剪框逻辑
      const cropBoxData = {
        width: cropBoxWidth,
        height: cropBoxHeight,
        left: (containerData.width - cropBoxWidth) / 2,
        top: (containerData.height - cropBoxHeight) / 2,
      };
      cropper.setCropBoxData(cropBoxData);
    }
  }, [imageResolution, width, height, fixedCropBox, cropBoxWidth, cropBoxHeight]);

  // 当图片分辨率加载完成后初始化裁剪框
  useEffect(() => {
    if (visible && imageSrc && imageResolution && cropperRef.current) {
      // 延迟执行确保Cropper完全初始化
      const timer = setTimeout(() => {
        initCropper();
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [visible, imageSrc, imageResolution, initCropper]);

  return (
    <div 
      className={styles.imageCropper}
      onDragOver={enableDragUpload ? handleDragOver : undefined}
      onDragLeave={enableDragUpload ? handleDragLeave : undefined}
      onDrop={enableDragUpload ? handleDrop : undefined}
    >
      {/* 模式切换开关 */}
      {enableModeSwitch && !disabled && (
        <div className={styles.modeSwitch}>
          <Radio.Group
            value={uploadMode}
            onChange={(e) => setUploadMode(e.target.value)}
            size="small"
            buttonStyle="solid"
            className={styles.modeSwitchGroup}
          >
            <Radio.Button value="crop" className={styles.modeButton}>
              <ScissorOutlined style={{ marginRight: 4 }} />
              <span>裁剪</span>
            </Radio.Button>
            <Radio.Button value="direct" className={styles.modeButton}>
              <UploadOutlined style={{ marginRight: 4 }} />
              <span>原图</span>
            </Radio.Button>
          </Radio.Group>
        </div>
      )}
      {/* 显示回显图片或裁剪后的预览 */}
      {displayImageUrl ? (
        <div
          className={styles.croppedPreview}
          onClick={disabled ? undefined : (enablePreview ? undefined : handleImageClick)}
          style={{ 
            cursor: disabled ? (enablePreview ? 'zoom-in' : 'default') : (enablePreview ? 'zoom-in' : 'pointer'),
            opacity: disabled ? 0.7 : 1,
          }}
        >
          <div className={styles.previewImage}>
            {enablePreview ? (
              <Image
                src={displayImageUrl}
                alt="图片预览"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                preview={enablePreview ? {
                  mask: (
                    <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ZoomInOutlined />
                      <span>查看大图</span>
                    </div>
                  ),
                } : false}
              />
            ) : (
              <img
                src={displayImageUrl}
                alt="图片预览"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {uploading && (
              <div className={styles.uploadingOverlay}>
                <LoadingOutlined />
                <div>正在上传...</div>
              </div>
            )}
            {!uploading && !enablePreview && !disabled && (
              <div className={styles.showImage}>
                <div>{originalFile ? '点击重新裁剪' : '点击查看大图'}</div>
              </div>
            )}
            {!uploading && !disabled && (
              <div className={styles.reCropOverlay} onClick={(e) => {
                e.stopPropagation();
                handleImageClick();
              }}>
                <ScissorOutlined />
                <div>{originalFile ? '点击重新裁剪' : '点击更换图片'}</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <Upload
          beforeUpload={disabled ? () => false : handleFileUpload}
          showUploadList={false}
          accept={fileTypes.join(',')}
          multiple={false}
          disabled={disabled}
          className={styles.uploadContainer}
        >
          <div className={styles.uploadButton}>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>点击上传</div>
            <div
              style={{
                fontSize: 10,
                color: '#666',
                marginTop: 4,
                padding: '0 5px',
                textAlign: 'center',
              }}
            >
              {picDesc}
            </div>
          </div>
        </Upload>
      )}

      <Modal
        title={
          <Space>
            <ScissorOutlined />
            <span>图片裁剪</span>
          </Space>
        }
        open={visible}
        onCancel={handleCropCancel}
        width={800}
        maskClosable={false}
        footer={
          <Space>
            <Button onClick={handleReset}>重置</Button>
            <Button onClick={handleCropCancel}>取消</Button>
            <Button
              type="primary"
              onClick={handleCropComplete}
              loading={uploading}
            >
              {autoUpload ? '裁剪并上传' : '确认裁剪'}
            </Button>
          </Space>
        }
        destroyOnClose
      >
        <div className={styles.cropperContainer}>
          {imageSrc ? (
            <div>
              <div style={{ marginBottom: '8px', color: '#666' }}>
                图片已加载
                {imageResolution && (
                  <span style={{ marginLeft: 8, fontSize: "10px" }}>
                    目前图片分辨率: {imageResolution.width} × {imageResolution.height}px，期望分辨率：{ width } x { height }px
                    {imageResolution.width === width && imageResolution.height === height && (
                      <span style={{ color: '#52c41a', marginLeft: 4 }}>（分辨率匹配）</span>
                    )}
                  </span>
                )}
                {imageSrc && originalFile && (
                  <div className={styles.fileInfo}>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      ✅ 已选择: {originalFile.name} (
                      {(originalFile.size / 1024 / 1024).toFixed(2)}MB)
                      {imageResolution && (
                        <span style={{ marginLeft: 8 }}>
                          | 分辨率: {imageResolution.width} × {imageResolution.height}px
                        </span>
                      )}
                    </Text>
                  </div>
                )}
                {!originalFile && imageResolution && (
                  <div className={styles.fileInfo}>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      分辨率: {imageResolution.width} × {imageResolution.height}px | 期望分辨率: {width} × {height}px
                    </Text>
                  </div>
                )}
              </div>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  background: '#000',
                }}
                // onDoubleClick={() => {
                //   // 双击切换拖拽模式
                //   const newMode = dragMode === 'move' ? 'crop' : 'move';
                //   setDragMode(newMode);
                //   // 更新 Cropper 的拖拽模式
                //   if (cropperRef.current?.cropper) {
                //     cropperRef.current.cropper.setDragMode(newMode);
                //   }
                // }}
              >
                <Cropper
                  src={imageSrc}
                  style={{ height: 400, width: '100%' }}
                  aspectRatio={
                    fixedCropBox ? cropBoxWidth / cropBoxHeight : aspectRatio
                  }
                  guides={true}
                  center={true}
                  cropBoxResizable={!fixedCropBox}
                  cropBoxMovable={true}
                  autoCropArea={1}
                  ref={cropperRef}
                  viewMode={1}
                  // dragMode={dragMode}
                  scalable={true}
                  rotatable={true}
                  zoomable={true}
                  zoomOnTouch={true}
                  zoomOnWheel={true}
                  wheelZoomRatio={0.1}
                  minCropBoxWidth={fixedCropBox ? cropBoxWidth : 50}
                  minCropBoxHeight={fixedCropBox ? cropBoxHeight : 50}
                  // 优化图片渲染质量
                  responsive={true}
                  restore={false}
                  checkOrientation={true}
                  modal={true}
                  highlight={true}
                  background={true}
                  autoCrop={true}
                  movable={true}
                  ready={() => {
                    console.log('Cropper 初始化完成');
                    // 延迟执行确保分辨率数据已加载
                    setTimeout(() => {
                      initCropper();
                    }, 100);
                  }}
                  cropstart={() => {
                    // console.log('开始裁剪');
                  }}
                  cropend={() => {
                    // console.log('结束裁剪');
                  }}
                  cropmove={() => {
                    // console.log('裁剪移动中');
                  }}
                  crop={() => {
                    // console.log('裁剪中');
                  }}
                />
              </div>
            </div>
          ) : (
            <div
              style={{
                height: 400,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                border: '1px dashed #d9d9d9',
                borderRadius: '4px',
              }}
            >
              <div style={{ color: '#999' }}>等待图片加载...</div>
            </div>
          )}
        </div>

        <div className={styles.cropperTips}>
          <Text type="secondary">
            💡 提示：拖拽调整裁剪区域，滚轮缩放图片 
            {/* 双击切换拖拽模式 */}
            {/* {dragMode === 'move' ? '（当前：移动图片）' : '（当前：创建裁剪区域）'} */}
            {/* {imageResolution && imageResolution.width === width && imageResolution.height === height && (
              <span style={{ color: '#52c41a', marginLeft: 8 }}>
                | 当前图片分辨率与期望分辨率完全匹配
              </span>
            )} */}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default ImageCropper;

